export const publicWishlistRoute = {
  labelKey: 'publicWishlist',
  href: '/public-wishlist',
};

export const REDIRECT_CHECKOUT_ROUTE = '/redirect-checkout';

export const accountRoutes = {
  ACCOUNT_DETAILS: '/account/account-details',
  PURCHASE_HISTORY: '/account/purchase-history',
  MEMBERSHIP: '/account/membership',
  MEMBERSHIP_UPDATE: '/account/membership/update',
  PAYMENT_METHODS: '/account/payment-methods',
  WISHLIST: '/account/wishlists',
} as const;

type AccountRouteKey = keyof typeof accountRoutes;
type AccountRouteValue = (typeof accountRoutes)[AccountRouteKey];

export const accountOptions = [
  { labelKey: 'accountDetails', href: accountRoutes.ACCOUNT_DETAILS },
  { labelKey: 'myGames', href: accountRoutes.PURCHASE_HISTORY },
  { labelKey: 'membership', href: accountRoutes.MEMBERSHIP },
  { labelKey: 'myWishlists', href: accountRoutes.WISHLIST },
] as const satisfies readonly {
  labelKey: string;
  href: AccountRouteValue;
}[];

export type AccountTab =
  | 'purchaseHistory'
  | 'accountDetails'
  | 'paymentMethods'
  | 'membershipManagement'
  | 'wishlists';

export const accountTabs = [
  { labelKey: 'purchaseHistory', href: accountRoutes.PURCHASE_HISTORY },
  { labelKey: 'accountDetails', href: accountRoutes.ACCOUNT_DETAILS },
  { labelKey: 'paymentMethods', href: accountRoutes.PAYMENT_METHODS },
  { labelKey: 'membershipManagement', href: accountRoutes.MEMBERSHIP },
  { labelKey: 'wishlists', href: accountRoutes.WISHLIST },
] as const satisfies readonly {
  labelKey: AccountTab;
  href: AccountRouteValue;
}[];

const legalRoutes = {
  termsAndConditions: { labelKey: 'termsAndConditions', href: '/legal/terms-of-use' },
  privacyPolicy: { labelKey: 'privacyPolicy', href: '/legal/privacy-policy' },
  safeShopping: { labelKey: 'safeShoppingGuarantee', href: '/legal/safe-shopping' },
  eula: { labelKey: 'eula', href: '/legal/eula' },
  refundPolicy: {
    labelKey: 'refundPolicy',
    href: 'https://support.wildtangent.com/hc/en-us/articles/200326376-WildTangent-Refund-Policy',
  },
  openSourceLicenses: { labelKey: 'openSourceLicenses', href: '/legal/open-source-licenses' },
};

export const pagesRoutes = {
  home: { labelKey: 'home', href: '/' },
  welcome: { labelKey: 'welcome', href: '/welcome' },
  games: { labelKey: 'games', href: '/games' },
  login: { labelKey: 'login', href: '/login' },
  logout: { labelKey: 'logout', href: '/logout' },
  resetPassword: { labelKey: 'resetPassword', href: '/reset-password' },
  changePassword: { labelKey: 'changePassword', href: '/change-password' },
  register: { labelKey: 'register', href: '/create-account' },
  verifyEmail: { labelKey: 'verifyEmail', href: '/verify-email' },
  emailVerified: { labelKey: 'emailVerified', href: '/email-verified' },
  search: { labelKey: 'search', href: '/search' },
  cart: { labelKey: 'cart', href: '/cart' },
  legal: legalRoutes,
  support: { labelKey: 'support', href: 'https://support.wildtangent.com/hc/en-us' },
  sitemap: { labelKey: 'sitemap', href: '/sitemap' },
  gameNews: { labelKey: 'gameNews', href: '/game-news' },
  joinWild: { labelKey: 'joinWild', href: '/join-wild' },
  joinWildTrial: { labelKey: 'joinWildTrial', href: '/join-wild/trial' },
  wildUnlimited: { labelKey: 'wildUnlimited', href: '/c/wild-unlimited-play' },
  freeToPlay: { labelKey: 'freeToPlay', href: '/c/free-to-play' },
  // Links the dynamic twin directly: the proxy rewrites /categories?sort=… there,
  // and a link to a search-dependent rewrite source confuses client routing (WTDEV-1758).
  newReleases: { labelKey: 'newReleases', href: '/categories/search?sort=newest' },
  gamesApp: { labelKey: 'gamesApp', href: '/gamesapp' },
  library: { labelKey: 'library', href: '/local/Library/index.html' },
  wildUnlimitedPlay: {
    labelKey: 'wildUnlimitedPlay',
    href: '/search?search_query=wild-unlimited-play',
  },
  onlineGames: { labelKey: 'onlineGames', href: '/search?search_query=online-games' },
  categories: { labelKey: 'categories', href: '/categories' },
} as const;

export const footerRoutes = {
  playGames: {
    labelKey: 'playGames',
    routes: [
      { labelKey: 'hiddenObject', href: '/c/hidden-object' },
      { labelKey: 'timeManagement', href: '/c/time-management' },
      { labelKey: 'match3', href: '/c/match-3' },
      { labelKey: 'cards&solitaire', href: '/c/cards' },
      { labelKey: 'casino', href: '/c/casino' },
    ],
  },
  legal: {
    labelKey: 'legal',
    routes: [
      legalRoutes.privacyPolicy,
      { labelKey: 'cookieSettings', href: '' },
      legalRoutes.termsAndConditions,
      legalRoutes.safeShopping,
      legalRoutes.eula,
      legalRoutes.refundPolicy,
      legalRoutes.openSourceLicenses,
    ],
  },
  info: {
    labelKey: 'info',
    routes: [
      { labelKey: 'imprint', href: 'https://company.wildtangent.com/#Imprint' },
      { labelKey: 'aboutUs', href: 'https://company.wildtangent.com/' },
      pagesRoutes.support,
      pagesRoutes.sitemap,
    ],
  },
} as const;
